import { useEffect, useRef } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import { ROLES } from '../core/constants/roles';
import {
  houseBroadcastInboxId,
  type HouseBroadcastDoc,
} from '../core/rules/houseBroadcast';

const RECENT_MS = 20 * 60 * 1000;

function inboxFields(row: HouseBroadcastDoc) {
  return {
    id: houseBroadcastInboxId(row.houseId, row.id),
    cat: 'messages' as const,
    sev: 'info' as const,
    urgent: true,
    title: row.title,
    body: row.fromName ? `${row.fromName}: ${row.body}` : row.body,
    meta: { houseId: row.houseId, fromUid: row.fromUid, broadcast: true },
  };
}

function ingestBroadcasts(
  rows: HouseBroadcastDoc[],
  seen: Set<string>,
  startedAt: number,
  userId: string,
  push: (fields: Record<string, unknown>, source?: string) => void,
) {
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    if (!row.publishedAtMs) continue;
    if (row.fromUid && row.fromUid === userId) {
      seen.add(row.id);
      continue;
    }
    if (row.publishedAtMs < startedAt - RECENT_MS) {
      seen.add(row.id);
      continue;
    }
    seen.add(row.id);
    push(inboxFields(row), 'push');
  }
}

/**
 * In-app inbox for house-wide broadcasts written by Cloud Functions.
 */
export function useHouseBroadcasts(opts: {
  userId: string;
  role?: string;
  houseIds: string[];
  push: (fields: Record<string, unknown>, source?: string) => void;
}) {
  const { userId, role, houseIds, push } = opts;
  const seenRef = useRef(new Set<string>());
  const startedAtRef = useRef(0);
  const pushRef = useRef(push);
  const userIdRef = useRef(userId);
  const platformGod = role === ROLES.GOD;

  useEffect(() => {
    pushRef.current = push;
    userIdRef.current = userId;
  });

  const houseKey = houseIds.filter(Boolean).sort().join(',');

  useEffect(() => {
    if (DATA_ADAPTER !== 'firebase') return undefined;
    if (!houseKey && !platformGod) return undefined;
    let cancelled = false;
    const unsubs: Array<() => void> = [];
    if (!startedAtRef.current) startedAtRef.current = Date.now();

    void import('../services/notifications/subscribeHouseBroadcasts').then(
      (mod) => {
        if (cancelled) return;
        const onRows = (rows: HouseBroadcastDoc[]) => ingestBroadcasts(
          rows,
          seenRef.current,
          startedAtRef.current,
          userIdRef.current,
          pushRef.current,
        );
        if (platformGod && !houseKey) {
          unsubs.push(mod.subscribeAllHouseBroadcasts(onRows));
          return;
        }
        for (const houseId of houseKey.split(',')) {
          unsubs.push(mod.subscribeHouseBroadcasts(houseId, onRows));
        }
      },
    );

    return () => {
      cancelled = true;
      unsubs.forEach((stop) => stop());
    };
  }, [houseKey, platformGod]);
}
